import moment from 'moment'
import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroDemoData'
import {
  currencyFractionDigits,
  toDisplayAmount,
} from 'src/feature/CardDetailHero/cardDetailHeroFormat'
import type { CurrencyFormatter } from 'src/localization/currencyFormatter'

/**
 * Formatting shared by the Statements tab's sections (EX-40600).
 *
 * Money crosses this boundary as `Money` (minor units + ISO-4217 currency) and
 * converts at the display edge via the hero's `toDisplayAmount`, which scales
 * per currency. A hardcoded ÷100 would misprice zero-decimal (JPY) and
 * three-decimal (BHD) currencies, so do not reintroduce one here.
 *
 * Scaling correctly is only half of it: `createCurrencyFormatter` defaults
 * `maximumFractionDigits` to 2 AND pins `minimumFractionDigits` to the same
 * value, so leaving it unset reintroduces the hardcode one layer down — BHD
 * 1234 minor would scale to 1.234 and then render as 1.23, a wrong figure, and
 * JPY would grow decimals it does not have. The digit count therefore has to be
 * passed explicitly, from the same per-currency source `toDisplayAmount` scales
 * by, so the two can never disagree.
 */

export const formatStatementAmount = (
  formatCurrency: CurrencyFormatter,
  money: Money,
): string =>
  formatCurrency({
    amount: toDisplayAmount(money),
    currency: money.currency as Parameters<CurrencyFormatter>[0]['currency'],
    maximumFractionDigits: currencyFractionDigits(money.currency),
  })

/** "June 2026" — the statement period label the design uses as the row title. */
export const formatStatementPeriod = (isoDate: string): string =>
  moment(isoDate).format('MMMM YYYY')

/** "Jul 15, 2026" — full dates, for due/paid lines and the modal description. */
export const formatStatementDate = (isoDate: string): string =>
  moment(isoDate).format('MMM D, YYYY')

/**
 * "Jul 15" — the year is dropped where the surrounding row already establishes
 * it, matching the design's row subtitle and modal summary.
 */
export const formatStatementDateShort = (isoDate: string): string =>
  moment(isoDate).format('MMM D')
